// The home page's living paintings: Santa on the phone in the hero, acting out a little pretend call
// in speech bubbles (or speaking for real when "Hear Santa" is pressed), Santa and Pipa on the two
// offer cards, and a sample Nice List certificate. The still images show first and stay if motion is
// reduced or the paintings fail to load.
import type { Mood } from './cabin.ts';
import { drawCertificate } from './certificate.ts';
import { elfName } from './elfname.ts';

const T = JSON.parse(document.getElementById('t')?.textContent || '{}');
const L = T.landing ?? { demo: [], demoName: '' };
const calm = matchMedia('(prefers-reduced-motion: reduce)').matches;
const $ = <E extends Element>(sel: string, root: ParentNode = document) => root.querySelector<E>(sel);

type Stage = { said: (text: string) => void; event: (id: string) => void };

/** Swap a frame's still image for its living painting once the layers have loaded. */
async function bringToLife(frame: HTMLElement, mood: () => Mood): Promise<Stage | null> {
  const canvas = $<HTMLCanvasElement>('canvas', frame)!;
  const { startPainted, startSwitchboard } = await import('./painted.ts');
  const start = frame.dataset.living === 'elf' ? startSwitchboard : startPainted;
  try {
    const stage = await start(canvas, mood, { name: L.demoName });
    requestAnimationFrame(() => frame.classList.add('alive'));
    return stage;
  } catch {
    return null;
  }
}

/** A voice that comes and goes in syllables, for the pretend calls. */
const syllables = (t: number) => 0.22 + 0.32 * Math.abs(Math.sin(t * 9.3)) * (0.6 + 0.4 * Math.sin(t * 2.1));

// ---------- the hero: Santa on the phone ----------
const hero = $<HTMLElement>('.lhero .frame');
if (hero && !calm) {
  const bubbleSanta = $<HTMLElement>('.bubble-santa', hero)!;
  const bubbleChild = $<HTMLElement>('.bubble-child', hero)!;
  const lines: { who: 'santa' | 'child'; text: string }[] = L.demo.map((l: any) => ({ ...l, text: l.text.replace('{name}', L.demoName) }));
  // Each line takes about as long as it would to say it; a short breath in between.
  const plan = lines.map((l) => ({ ...l, dur: (l.who === 'santa' ? 1.3 : 0.9) + l.text.split(/\s+/).length * (l.who === 'santa' ? 0.36 : 0.3) }));
  const cycle = plan.reduce((s, l) => s + l.dur + 0.7, 0) + 2.5;

  let stage: Stage | null = null;
  let voice = 0; // the real "Hear Santa" level, when playing
  let playing = false;
  let shown = -1;
  const t0 = performance.now();

  const current = (t: number) => {
    let at = t % cycle;
    for (let i = 0; i < plan.length; i++) {
      if (at < plan[i].dur) return { i, at };
      at -= plan[i].dur + 0.7;
      if (at < 0) return null;
    }
    return null;
  };

  const mood = (): Mood => {
    if (playing) return { santa: voice, child: 0 };
    const t = (performance.now() - t0) / 1000;
    const c = current(t);
    // bubbles follow the script
    const i = c ? c.i : -1;
    if (i !== shown) {
      shown = i;
      bubbleSanta.classList.remove('show');
      bubbleChild.classList.remove('show');
      if (c) {
        const l = plan[c.i];
        const b = l.who === 'santa' ? bubbleSanta : bubbleChild;
        b.textContent = l.text;
        b.classList.add('show');
        if (l.who === 'santa') stage?.said(l.text);
        else stage?.said(L.demoName); // he looks at the child when they speak
      }
    }
    if (!c) return { santa: 0, child: 0 };
    return plan[c.i].who === 'santa' ? { santa: syllables(t), child: 0 } : { santa: 0, child: 0.45 };
  };

  const go = () => void bringToLife(hero, mood).then((s) => (stage = s));
  if (document.readyState === 'complete') go();
  else addEventListener('load', go, { once: true });

  // the call timer: it has been ringing a while already
  const timer = $<HTMLElement>('[data-timer]');
  let secs = 107;
  const tick = () => timer && (timer.textContent = `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`);
  tick();
  setInterval(() => (secs++, tick()), 1000);

  // "Hear Santa": his real voice, and the painting speaks along with it
  const hear = $<HTMLButtonElement>('[data-hear]');
  if (hear) {
    const label = $<HTMLElement>('[data-hear-label]', hear)!;
    const idle = label.textContent;
    let audio: HTMLAudioElement | null = null;
    let raf = 0;
    const stop = () => {
      playing = false;
      voice = 0;
      cancelAnimationFrame(raf);
      hear.classList.remove('on');
      label.textContent = idle;
    };
    hear.addEventListener('click', () => {
      if (playing && audio) {
        audio.pause();
        return stop();
      }
      if (!audio) {
        audio = new Audio(hear.dataset.src);
        audio.addEventListener('ended', stop);
        audio.addEventListener('pause', stop);
        audio.addEventListener('error', () => ((hear.hidden = true), stop()));
        const ac = new AudioContext();
        const an = ac.createAnalyser();
        an.fftSize = 512;
        ac.createMediaElementSource(audio).connect(an);
        an.connect(ac.destination);
        const buf = new Uint8Array(an.fftSize);
        const level = () => {
          an.getByteTimeDomainData(buf);
          let sum = 0;
          for (const v of buf) sum += ((v - 128) / 128) ** 2;
          voice = Math.min(1, Math.sqrt(sum / buf.length) * 4);
          raf = requestAnimationFrame(level);
        };
        audio.addEventListener('play', () => {
          void ac.resume();
          raf = requestAnimationFrame(level);
        });
      }
      playing = true;
      shown = -2;
      bubbleSanta.classList.remove('show');
      bubbleChild.classList.remove('show');
      hear.classList.add('on');
      label.textContent = hear.dataset.stop || '';
      stage?.said('ho ho');
      void audio.play().catch(stop);
    });
  }
}

// ---------- the offer cards and the call pages: Santa and Pipa, alive while you look at them ----------
for (const frame of document.querySelectorAll<HTMLElement>('.offer .frame, .page-art.living')) {
  if (calm) break;
  const elf = frame.dataset.living === 'elf';
  const t0 = performance.now() + Math.random() * 4000;
  // They're mid-call: talking for a while, then listening to the child.
  const mood = (): Mood => {
    const t = (performance.now() - t0) / 1000;
    const talking = t % 9 < (elf ? 5 : 4.5);
    return talking ? { santa: syllables(t), child: 0 } : { santa: 0, child: 0.4 };
  };
  const io = new IntersectionObserver(
    ([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      void bringToLife(frame, mood);
    },
    { rootMargin: '300px' },
  );
  io.observe(frame);
}

// ---------- a sample Nice List certificate ----------
const cert = $<HTMLCanvasElement>('#demo-cert');
if (cert && T.call?.after?.cert) {
  const io = new IntersectionObserver(
    ([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const name = L.demoName;
      const n = new Date();
      void drawCertificate(
        cert,
        {
          name,
          age: 6,
          place: '',
          proud: L.demoProud.charAt(0).toLowerCase() + L.demoProud.slice(1),
          wishes: L.demoWish.toLowerCase(),
          elf: elfName(name, T.elf).name,
          date: n.toLocaleDateString(T.lang, { day: 'numeric', month: 'long', year: 'numeric' }),
          number: '1 224 024',
        },
        T.call.after.cert,
      ).then(() => cert.classList.add('drawn'));
    },
    { rootMargin: '400px' },
  );
  io.observe(cert);
}
